import React, { ChangeEvent, ReactElement, useEffect, useState } from 'react';
import classnames from 'classnames';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm, FieldError } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  ActionCreatorWithOptionalPayload,
  ActionCreatorWithPayload,
} from '@reduxjs/toolkit';
import { Alert } from '../../../../../../../../../../components/CiscoUi';
import {
  ControlledCheckbox,
  ControlledSelect,
  ControlledTextfield,
} from '../../../../../../../../../../components';
import { PrimaryButton } from '../../../../../../../../../../components/Buttons/PrimaryButton';
import { editvmNetworkInterfaceFieldsSchema } from '../../../../../../../../../../validation';
import {
  buildTrunkedNetworkInterface,
  getNetworkOptions,
  getTrunkedNetworkUids,
  getTrunkedNetworkOptions,
  resolveTrunkedNetworksSelection,
} from '../../../../../Configure/Utils';
import {
  findInventoryNetwork,
  findNetworkName,
  getNicTypeOptions,
  hasNicLimitReached,
} from '../../utils';
import { NicLimitError } from '../NicLimitError';
import { DEFAULT_NETWORK_MAX_CONNECTIONS } from '../../../../../../../constants';
import { NetworkingFormData } from '../NetworkingForm';
import { applyDhcpFormChange } from '../NetworkingForm/utils/utils';
import styles from '../NetworkingForm/NetworkingForm.module.css';
import { ControlledToggleButtonGroup } from '../../../../../../../../../../components/Form/ControlledToggleButtonGroup';
import { ControlledMultipleSelect } from '../../../../../../../../../../components/Form/ControlledMultipleSelect';
import { useEditNicTrunkedMode } from './hooks/use-edit-nic-trunked-mode';
import { useStashTrunkedNetworks } from './hooks/use-stash-trunked-networks';
import { useTriggerIpValidation } from './hooks/use-trigger-ip-validation';
import {
  applyTrunkedEnabledFormChange,
  getEditNicFormOriginalValues,
  isEditNicFormUnchanged,
  isSwitchingToTrunk,
} from './utils/utils';
import { TrunkedNicRulesWarningModal } from './components/TrunkedNicRulesWarningModal';

interface EditNetworkingFormProps {
  updateVMNetworkInterface: ActionCreatorWithPayload<
    VmNetworkInterfaceToEdit,
    string
  >;
  networks: Network[];
  nicTypes: VmNetworkInterfaceType[];
  nicToEdit: VmNetworkInterfaceToEdit;
  setNicToEdit: ActionCreatorWithOptionalPayload<
    VmNetworkInterfaceToEdit | undefined,
    string
  >;
  setShowTrunkedNicRulesWarning: ActionCreatorWithPayload<boolean, string>;
  showTrunkedNicRulesWarning: boolean;
  limits: Limits;
  vmNetworkInterfaces?: VmNetworkingToEdit;
  vmToEdit?: VirtualMachine;
}

export function EditNetworkingForm({
  networks,
  nicTypes,
  nicToEdit,
  setNicToEdit,
  setShowTrunkedNicRulesWarning,
  showTrunkedNicRulesWarning,
  limits,
  updateVMNetworkInterface,
  vmNetworkInterfaces,
  vmToEdit,
}: EditNetworkingFormProps): ReactElement {
  const { t } = useTranslation();
  const [isNwLimitReached, setIsNwLimitReached] = useState<boolean>(false);
  const [limit, setLimit] = useState<number>(0);
  const [subnet, setSubnet] = useState<string>('');
  const formSectionClasses = classnames('col-2', styles.formSection);
  const checkBoxClasses = classnames('col-1', styles.checkboxGroup);
  const buttonGroupClasses = classnames('col-4', styles.formSection);

  const originalValues = getEditNicFormOriginalValues(nicToEdit);

  const { control, errors, handleSubmit, formState, setValue, trigger, watch } =
    useForm<NetworkingFormData>({
      defaultValues: originalValues,
      mode: 'all',
      resolver: yupResolver(
        editvmNetworkInterfaceFieldsSchema(limits, subnet, networks),
      ),
    });

  const confirmNicUpdate: SubmitHandler<NetworkingFormData> = async (
    formData,
  ) => {
    if (formData.trunked) {
      const trunkedNetworks = formData.trunkedNetworks ?? [];
      const trunkedInterface = buildTrunkedNetworkInterface(
        networks,
        trunkedNetworks,
      );

      updateVMNetworkInterface({
        assignDhcp: false,
        inUse: nicToEdit?.inUse || false,
        ipAddress: '',
        macAddress: formData.macAddress,
        trunk: trunkedInterface,
        type: formData.type,
        uid: nicToEdit.uid,
      });
      setNicToEdit(undefined);
      return;
    }

    const networkName = findNetworkName(networks, formData);
    if (networkName) {
      updateVMNetworkInterface({
        assignDhcp: formData.assignDhcp,
        inUse: nicToEdit?.inUse || false,
        ipAddress: formData.ipAddress,
        macAddress: formData.macAddress,
        network: {
          inventoryNetwork: findInventoryNetwork(networks, formData),
          name: networkName,
          uid: formData.networkName ?? '',
        },
        trunk: { enabled: false },
        type: formData.type,
        uid: nicToEdit.uid,
      });
      setNicToEdit(undefined);
      return;
    }
  };

  const onSaveClicked: SubmitHandler<NetworkingFormData> = async (formData) => {
    if (isSwitchingToTrunk(formData, nicToEdit) && nicToEdit.inUse) {
      setShowTrunkedNicRulesWarning(true);
      return;
    }

    await confirmNicUpdate(formData);
  };

  const handleConfirmTrunkedChange = () => {
    setShowTrunkedNicRulesWarning(false);
    handleSubmit(confirmNicUpdate)();
  };

  const { isValid } = formState;
  const formValues = watch();
  const hasChanges = !isEditNicFormUnchanged(formValues, originalValues);

  useEffect(() => {
    triggerNicLimitCheck(control.getValues().networkName);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vmNetworkInterfaces?.length]);

  const triggerNicLimitCheck = (selectedNwUid?: string) => {
    if (selectedNwUid) {
      const savedNics = vmToEdit?.vmNetworkInterfaces || [];
      const nicsToSave = vmNetworkInterfaces || [];
      const selectedNetwork = networks.find((nw) => nw.uid === selectedNwUid);
      const isEditing =
        !!nicToEdit && nicToEdit?.network?.uid === selectedNwUid;
      const limitReached = hasNicLimitReached(
        savedNics,
        nicsToSave,
        selectedNetwork!,
        isEditing,
      );
      setIsNwLimitReached(limitReached);
      if (limitReached) {
        setLimit(
          selectedNetwork?.vmConnectionInfo?.maxConnections ||
            DEFAULT_NETWORK_MAX_CONNECTIONS,
        );
      }

      selectedNetwork
        ? setSubnet(selectedNetwork.inventoryNetwork.subnet)
        : setSubnet('');
    }
  };

  const handleDhcpChange = async (e: ChangeEvent<HTMLInputElement>) => {
    await applyDhcpFormChange(
      e.target.value,
      control.getValues().ipAddress,
      setValue,
      trigger,
    );
  };

  const watchAssignDhcp = watch('assignDhcp');
  const watchNetworkName = watch('networkName');

  const isTrunked = useEditNicTrunkedMode({
    onTrunkedDisabled: triggerNicLimitCheck,
    originalValues,
    setValue,
    trigger,
    watch,
  });

  const stashedTrunkedNetworksRef = useStashTrunkedNetworks({
    initialTrunkedNetworks: originalValues.trunkedNetworks ?? [],
    isTrunked,
    watch,
  });

  useTriggerIpValidation({ trigger, watchNetworkName });

  const handleTrunkedChange = (value: boolean) => {
    applyTrunkedEnabledFormChange(
      value,
      stashedTrunkedNetworksRef.current,
      nicToEdit,
      originalValues,
      setValue,
    );
  };

  const handleTrunkedNetworksChange = (next: string[]) =>
    resolveTrunkedNetworksSelection(
      control.getValues().trunkedNetworks ?? [],
      next,
      getTrunkedNetworkUids(networks),
    );

  const showError = isNwLimitReached && !!control.getValues().networkName;
  return (
    <>
      <div
        aria-label="VM Edit Networking form"
        className={styles.formContainer}
      >
        <div className={styles.trunkedRow}>
          <ControlledToggleButtonGroup
            control={control}
            name="trunked"
            customOnChange={handleTrunkedChange}
            options={{
              left: {
                label: t('networks.trunk.access'),
                value: false,
              },
              right: {
                label: t('networks.trunk.label'),
                tooltip: {
                  position: 'down',
                  text: t('virtualMachines.edit.networking.trunked.tooltip'),
                },
                value: true,
              },
            }}
          />
        </div>
        <div className={styles.root}>
          {isTrunked ? (
            <div
              className={classnames(
                formSectionClasses,
                styles.controlledMultipleSelectSection,
              )}
            >
              <ControlledMultipleSelect
                control={control}
                name="trunkedNetworks"
                label={t('networks.trunk.networkOrVlans')}
                horizontal={false}
                options={getTrunkedNetworkOptions(networks)}
                customOnChange={handleTrunkedNetworksChange}
                required
                testId="edit-vm-networking-trunked-networks"
                error={
                  (errors.trunkedNetworks as FieldError | undefined)?.message
                }
              />
            </div>
          ) : (
            <div className={formSectionClasses}>
              <ControlledSelect
                className={showError ? styles.errorSelect : ''}
                control={control}
                label={t('virtualMachines.edit.networking.network')}
                horizontal={false}
                options={getNetworkOptions(networks)}
                name="networkName"
                loading={false}
                testId="edit-vm-networking-nic-network-select"
                required={true}
                error={errors?.networkName?.message}
                customOnChange={(event) => {
                  triggerNicLimitCheck(event.target.value);
                }}
              />
            </div>
          )}
          <div className={formSectionClasses}>
            <ControlledSelect
              control={control}
              label={t('virtualMachines.edit.networking.type')}
              horizontal={false}
              options={getNicTypeOptions(nicTypes)}
              name="type"
              loading={false}
              testId="edit-vm-networking-nic-type-select"
              required={true}
              error={errors?.type?.message}
            />
          </div>
          <div className={formSectionClasses}>
            <ControlledTextfield
              control={control}
              error={errors?.macAddress?.message}
              label={t('virtualMachines.edit.networking.macAddress')}
              name="macAddress"
              required={false}
              testId="edit-vm-networking-mac-address-input"
            />
          </div>
          <div aria-label="Add NIC IP Address" className={formSectionClasses}>
            <ControlledTextfield
              control={control}
              error={errors?.ipAddress?.message}
              label={t('virtualMachines.edit.networking.ipAddress')}
              name="ipAddress"
              required={watchAssignDhcp}
              disabled={isTrunked}
              testId="edit-vm-networking-ip-address-input"
            />
          </div>
          <div className={checkBoxClasses}>
            <p>{t('virtualMachines.edit.networking.dhcp.title')} </p>
            <ControlledCheckbox
              className={styles.checkbox}
              control={control}
              customOnChange={async (e) => handleDhcpChange(e)}
              id="assignDhcp"
              label=""
              testId="edit-vm-networking-enable-dhcp"
              disabled={isTrunked}
            />
          </div>
          <div className={buttonGroupClasses}>
            <PrimaryButton
              className={styles.button}
              type="button"
              disabled={!hasChanges || !isValid || showError}
              onClick={handleSubmit(onSaveClicked)}
              testId="edit-vm-networking-save-button"
            >
              {t('buttons.save')}
            </PrimaryButton>

            <PrimaryButton
              className={styles.button}
              type="button"
              colour="danger"
              onClick={() => setNicToEdit(undefined)}
              testId="edit-vm-networking-cancel-button"
            >
              {t('buttons.cancel')}
            </PrimaryButton>
          </div>
        </div>
      </div>
      {showError && <NicLimitError limit={limit} />}

      <Alert colour="info">
        {t('virtualMachines.edit.networking.warnings.changesInfo')}
      </Alert>

      <TrunkedNicRulesWarningModal
        onCancel={() => setShowTrunkedNicRulesWarning(false)}
        onConfirm={handleConfirmTrunkedChange}
        showTrunkedNicRulesWarning={showTrunkedNicRulesWarning}
      />
    </>
  );
}
